import { useLazyQuery } from '@apollo/client';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { GetInternalAuditReportTestResultsByControlIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ContentTable } from 'pdfmake/interfaces';
import { useTranslation } from 'react-i18next';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';

import { toLocalDate } from '@/utils/dateUtils';
import { createTable, tableHeaders } from '@/utils/pdf/table';
import { useFormConfigRegistry } from '@/utils/table/hooks/form/useFormConfigRegistry';

const useInternalAuditPerformanceTableExporter = (
  controlId: string,
  getStandardFieldLabel: GetStandardFormFieldLabel
): [() => Promise<ContentTable>, boolean] => {
  const { t } = useTranslation(['common']);

  const [getTestResults, getTestResultsResult] = useLazyQuery(
    GetInternalAuditReportTestResultsByControlIdDocument,
    {
      variables: {
        controlId: controlId,
      },
    }
  );
  const { getLabel: getEffectivenessLabel } = useRating('effectiveness');

  const formRegistry = useFormConfigRegistry();
  const testTypeAvailable =
    formRegistry.control_test_internal_audit_result.TestType.available !==
    false;

  const createExportTable = async () => {
    const { data: testResultData } = await getTestResults();

    const testTypeLookup = t('testTypes');
    const rows = testResultData?.control_test_internal_audit_result ?? [];

    type Row = (typeof rows)[number];

    const columns: {
      visible: boolean;
      width: string | number;
      header: string;
      cell: (row: Row) => string | null | undefined;
    }[] = [
      {
        visible: true,
        width: '*',
        header: getStandardFieldLabel(
          'control_test_internal_audit_result',
          'Title'
        ),
        cell: (au) => au.Title ?? '-',
      },
      {
        visible: true,
        width: 50,
        header: getStandardFieldLabel(
          'control_test_internal_audit_result',
          'TestDate'
        ),
        cell: (au) => toLocalDate(au.TestDate),
      },
      {
        visible: testTypeAvailable,
        width: 70,
        header: getStandardFieldLabel(
          'control_test_internal_audit_result',
          'TestType'
        ),
        cell: (au) =>
          au.TestType
            ? testTypeLookup[au.TestType as keyof typeof testTypeLookup]
            : '-',
      },
      {
        visible: true,
        width: 90,
        header: getStandardFieldLabel(
          'control_test_internal_audit_result',
          'OverallEffectiveness'
        ),
        cell: (au) => getEffectivenessLabel(au.OverallEffectiveness),
      },
      {
        visible: true,
        width: 70,
        header: getStandardFieldLabel(
          'control_test_internal_audit_result',
          'Submitter'
        ),
        cell: (au) => au.submitter?.FriendlyName ?? '',
      },
      {
        visible: true,
        width: 50,
        header: getStandardFieldLabel('internal_audit_report', 'NextTestDate'),
        cell: (au) =>
          toLocalDate(
            au.parents
              .filter((c) => c.internalAuditReport)
              .map((c) => c.internalAuditReport)[0]?.NextTestDate
          ) ?? '',
      },
    ];

    const visible = columns.filter((c) => c.visible);

    return createTable({
      widths: visible.map((c) => c.width),
      body: [
        tableHeaders(visible.map((c) => c.header)),
        ...rows.map((au) => visible.map((c) => c.cell(au) ?? '')),
      ],
    });
  };

  return [createExportTable, getTestResultsResult.loading];
};

export default useInternalAuditPerformanceTableExporter;
